/* ============================================================
   The Fool — inscription newsletter, envoi direct à Klaviyo
   API client : https://a.klaviyo.com/client/subscriptions
   Seule la clé PUBLIQUE est utilisée (elle est publique par
   conception, Klaviyo l'expose dans le source de chaque page).
   ============================================================ */

const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
const KLAVIYO_REVISION = '2026-07-15';

class KlaviyoForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.status = this.querySelector('.newsletter__status');
    this.button = this.querySelector('.newsletter__submit');
    if (!this.form) return;

    this.messages = {
      loading: this.text('[data-msg-loading]', 'Envoi…'),
      invalid: this.text('[data-msg-invalid]', 'Cette adresse e-mail semble incorrecte.'),
      error: this.text('[data-msg-error]', "L'inscription n'a pas pu aboutir. Réessayez dans un instant."),
    };

    this.buttonLabel = this.button ? this.button.textContent.trim() : '';
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
  }

  text(selector, fallback) {
    const node = this.querySelector(selector);
    const value = node?.textContent.trim();
    return value || fallback;
  }

  onSubmit(event) {
    event.preventDefault();

    const data = new FormData(this.form);

    // Piège à robots : si rempli, on simule un succès sans rien envoyer.
    if ((data.get('website') || '').trim() !== '') {
      this.showSuccess();
      return;
    }

    const email = (data.get('email') || '').trim();
    if (!this.isValidEmail(email)) {
      this.showError(this.messages.invalid);
      this.form.querySelector('input[type="email"]')?.focus();
      return;
    }

    this.submit(email, (data.get('first_name') || '').trim());
  }

  isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  }

  async submit(email, firstName) {
    const { companyId, listId, source } = this.dataset;
    if (!companyId || !listId) {
      this.showError(this.messages.error);
      return;
    }

    this.setBusy(true);

    const attributes = {
      email,
      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
    };
    if (firstName) attributes.first_name = firstName;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes } },
        },
        relationships: {
          list: { data: { type: 'list', id: listId } },
        },
      },
    };

    if (source) payload.data.attributes.custom_source = source;

    try {
      const response = await fetch(
        `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: KLAVIYO_REVISION,
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.status === 202 || response.ok) {
        this.showSuccess();
        this.track(email);
        return;
      }

      this.showError(this.messages.error);
      if (window.console) {
        console.warn('[newsletter] Klaviyo a répondu', response.status, await response.text());
      }
    } catch (error) {
      this.showError(this.messages.error);
      if (window.console) console.warn('[newsletter] échec réseau', error);
    } finally {
      this.setBusy(false);
    }
  }

  // Rattache l'inscription au profil suivi par klaviyo.js, si l'app embed est active.
  track(email) {
    if (!window.klaviyo || typeof window.klaviyo.push !== 'function') return;
    try {
      window.klaviyo.push(['identify', { $email: email }]);
    } catch {
      /* sans effet si klaviyo.js n'est pas chargé */
    }
  }

  setBusy(busy) {
    if (!this.button) return;
    this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
    this.button.disabled = busy;
    this.button.textContent = busy ? this.messages.loading : this.buttonLabel;
    if (busy) this.hideStatus();
  }

  hideStatus() {
    if (!this.status) return;
    this.status.hidden = true;
    this.status.textContent = '';
    this.status.removeAttribute('data-state');
  }

  showError(message) {
    if (!this.status) return;
    this.status.hidden = false;
    this.status.textContent = message;
    this.status.setAttribute('data-state', 'error');
  }

  showSuccess() {
    const template = this.querySelector('template[data-success]');
    this.hideStatus();
    this.form.remove();
    if (template) this.prepend(template.content.cloneNode(true));
    this.querySelector('.newsletter__success')?.focus?.();
  }
}

customElements.define('klaviyo-form', KlaviyoForm);
